import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useStore } from '@utils/event-store/useEventData';
import QuestionIcon from '@assets/imgs/survey/register-icon.png';
import ApproveIcon from '@assets/imgs/survey/approve-icon.png';
import StaticicsIcon from '@assets/imgs/survey/statistics-icon.png';
import MarketIcon from '@assets/imgs/survey/market-icon.png';
export const typeMap: { [key: string]: { [key: string]: string } } = {
  audience: {
    type: 'b_edit_attendee',
    path: '/event/[eventid]/members/audience',
    reportCode: 'e#event_overview#registrant_link#click',
  },
  approve: {
    type: 'wait_approving_num',
    path: '/event/[eventid]/registration/approval',
    reportCode: 'e#event_overview#review_link#click',
  },
  question: {
    type: 'question',
    path: '/event/[eventid]/registration/question',
    reportCode: '',
  },
  marketing: {
    type: 'marketing',
    path: '/event/[eventid]/marketing',
    reportCode: '',
  },
  guest: {
    type: 'b_edit_guest',
    path: '/event/[eventid]/members/guest',
    reportCode: 'e#event_overview#panelist_link#click',
  },
  venue: {
    type: 'b_edit_venue',
    path: '/event/[eventid]/venue/stage',
    reportCode: 'e#event_overview#venue_link#click',
  },
  ticket: {
    type: 'b_edit_ticket',
    path: '/event/[eventid]/registration/tickets',
    reportCode: 'e#event_overview#ticket_link#click',
  },
  playback: {
    type: 'b_edit_replay',
    path: '/event/[eventid]/playback',
    reportCode: 'e#event_overview#replay_link#click',
  },
  basic: {
    type: 'b_edit_basic',
    path: '/event/[eventid]/basic',
    reportCode: 'e#event_overview#basic_link#click',
  },
  scheduler: {
    type: 'b_edit_scheduler',
    path: '/event/[eventid]/schedule',
    reportCode: 'e#event_overview#schedule_link#click',
  },
  register: {
    type: 'b_edit_register_config',
    path: '/event/[eventid]/registration/register',
    reportCode: 'e#event_overview#page_link#click',
  },
  publish: {
    type: 'b_edit_publish',
    path: '',
    reportCode: 'e#event_overview#publish_button#click',
  },
};
const defaultPlayList = [
  {
    title: '报名问题',
    desc: '设置用户报名前需要填写的问题，帮助您了解报名用户的相关信息',
    type: 'question',
    imgUrl: QuestionIcon,
    name: 'register_question',
  },
  {
    title: '报名审批',
    desc: '开启相关门票的报名审批后，可对报名用户进行准入权限的审核',
    type: 'approve',
    imgUrl: ApproveIcon,
    name: 'approval',
  },
  {
    title: '渠道统计',
    desc: '设置活动报名的不同渠道，支持查看不同渠道的访问人数、报名人数、购票人数等信息',
    type: 'marketing',
    imgUrl: StaticicsIcon,
    name: 'marketing',
  },
  {
    title: '活动场地',
    desc: '活动场地支持直播大厅和讨论室，满足多会场形式的活动组织需要',
    type: 'venue',
    imgUrl: MarketIcon,
    name: 'venue',
  },
];
const defaultProgressList = [
  {
    title: '基础信息',
    desc: '为活动设置主题、时间、类型、标签等',
    isEdit: true,
    src: 'https://wemeet-activity-test-1258344699.cos.ap-guangzhou.myqcloud.com/activity-frontend_image/survey/basic-info.png',
    buttonName: '前往',
    name: 'b_edit_basic',
    type: 'basic',
  },
  {
    title: '活动嘉宾',
    desc: '上传活动嘉宾的头像、介绍和头衔',
    isEdit: false,
    src: 'https://wemeet-activity-test-1258344699.cos.ap-guangzhou.myqcloud.com/activity-frontend_image/survey/audience.png',
    buttonName: '前往',
    name: 'b_edit_guest',
    type: 'guest',
  },
  {
    title: '活动日程',
    desc: '设置各个日程的时间、场地、嘉宾和详情介绍',
    isEdit: false,
    src: 'https://wemeet-activity-test-1258344699.cos.ap-guangzhou.myqcloud.com/activity-frontend_image/survey/schedule.png',
    buttonName: '前往',
    name: 'b_edit_scheduler',
    type: 'scheduler',
  },
  {
    title: '门票管理',
    desc: '设置门票种类、价格和数量',
    isEdit: false,
    src: 'https://wemeet-activity-test-1258344699.cos.ap-guangzhou.myqcloud.com/activity-frontend_image/survey/ticket.png',
    buttonName: '前往',
    name: 'b_edit_ticket',
    type: 'ticket',
  },

  {
    title: '报名页装修',
    desc: '设置报名页样式，支持经典样式、高级定制',
    isEdit: false,
    src: 'https://wemeet-activity-test-1258344699.cos.ap-guangzhou.myqcloud.com/activity-frontend_image/survey/registration.png',
    buttonName: '前往',
    name: 'b_edit_register_config',
    type: 'register',
  },
  {
    title: '预览及发布',
    desc: '预览活动效果&发布活动',
    isEdit: false,
    src: 'https://wemeet-activity-test-1258344699.cos.ap-guangzhou.myqcloud.com/activity-frontend_image/survey/publish.png',
    name: 'b_edit_publish',
    type: 'publish',
  },
];
const useProgressOption = (report?: Function) => {
  const router = useRouter();
  const { progressInfo, advancedList, eventInfo, progressList: configList } = useStore();
  // 获取必填步骤step
  const progressList = useMemo(() => {
    if (!progressInfo || !configList) return defaultProgressList;
    const list = configList.map((item) => {
      const arr = defaultProgressList.filter((i) => i.type === item?.name);
      return arr?.[0];
    });
    return list.map((item) => ({
      ...item,
      isEdit: progressInfo ? progressInfo[item?.name || ''] : item?.isEdit,
    }));
  }, [progressInfo, configList]);

  // 获取高级玩法step
  const step3Arr = useMemo(() => {
    const arr = [...defaultPlayList];
    if (!advancedList) return arr;
    return advancedList.map((item) => {
      const arrlist = arr.filter((i) => i.name === item?.name);
      return arrlist?.[0];
    });
  }, [advancedList]);

  // 当前链接跳转方法
  const btnRedirect = useCallback(
    (type: string) => {
      let path = typeMap[type]?.path;
      const code = typeMap[type]?.reportCode;
      if (!type || !path) return;
      if (type === 'venue' && !eventInfo?.venue_type?.includes(1)) {
        path = '/event/[eventid]/venue/session';
      }
      report?.(code);
      const str = path.replace(/\[eventid\]/, eventInfo?.event_id || '');
      router.push(str);
    },
    [eventInfo?.event_id, eventInfo?.venue_type],
  );

  // 完成度白分比
  const isAllPercent = useMemo(() => {
    let percentNum = 0;
    if (!progressList.length || !eventInfo?.event_id) return '';
    if (!progressInfo?.b_edit_basic) return ((1 / progressList.length) * 100).toFixed(0);
    progressList.forEach((item) => {
      if (item?.isEdit) percentNum += 1;
    });
    return ((percentNum / progressList.length) * 100).toFixed(0);
  }, [eventInfo?.event_id, progressInfo, progressList]);

  const canPublish = useMemo(() => {
    let percentNum = 0;
    if (!progressList.length || !progressInfo || !eventInfo?.event_id) return false;
    progressList.forEach((item) => {
      if (item?.isEdit) percentNum += 1;
    });
    return percentNum + 1 === progressList.length;
  }, [eventInfo?.event_id, progressInfo, progressList]);

  return {
    defaultPlayList,
    progressList,
    step3Arr,
    isAllPercent,
    btnRedirect,
    canPublish,
  };
};
export default useProgressOption;
